import Button from 'components/commercetools-ui/atoms/button';
import Link from 'components/commercetools-ui/atoms/link';
import Typography from 'components/commercetools-ui/atoms/typography';
import Image, { ImageProps } from 'frontastic/lib/image';
import { memo, useEffect, useMemo, useState } from 'react';
import { Reference } from 'types/reference';
import Video from '../video';

export interface HeroProps {
  image: ImageProps;
  title: string;
  ctaLabel?: string;
  ctaReference?: Reference;
  ctaDataAnalytics?: string;
  youTubeLink?: string;
  ytDataAnalytics?: string;
  isLoopVideo: boolean;
  isAudio: boolean;
  isAutoPlay: boolean;
  body?: string;
  secondCtaLabel?: string;
  secondCtaReference?: Reference;
  secondCtaDataAnalytics?: string;
  height?: 'half' | 'full';
  horizontalAlign?: 'left' | 'center';
  verticalAlign?: 'top' | 'center' | 'bottom';
  imageBrightness?: boolean;
  ctaStyle?: 'primary' | 'secondary';
  secondCtaStyle?: 'primary' | 'secondary';
  sectionId?: string;
  fontWeight?: 'medium' | 'bold';
  overlayType?:
    | 'none'
    | 'solid'
    | 'gradient-top-down'
    | 'gradient-bottom-up'
    | 'gradient-left-right'
    | 'gradient-right-left';
  overlayColor?: 'padi-gray-darkest' | 'white';
}

const buttonCommonClasses = 'md:px-48 md:py-12 h-40';

const Hero: React.FC<HeroProps> = ({
  image,
  title,
  ctaLabel,
  ctaReference,
  youTubeLink,
  isLoopVideo,
  isAudio,
  isAutoPlay,
  body,
  secondCtaLabel,
  secondCtaReference,
  height,
  horizontalAlign,
  verticalAlign,
  imageBrightness,
  ctaStyle,
  secondCtaStyle,
  ctaDataAnalytics,
  secondCtaDataAnalytics,
  ytDataAnalytics,
  sectionId,
  fontWeight,
  overlayType = 'none',
  overlayColor = 'white',
}) => {
  const cropFocus = useMemo(() => {
    if (image?.gravity?.coordinates) {
      const right = Math.ceil((image.gravity?.coordinates.x / (image.media?.width as number)) * 100);
      const top = Math.ceil((image.gravity?.coordinates.y / (image.media?.height as number)) * 100);

      return `right ${right.toString()}% top ${top.toString()}%`;
    }

    if (image?.gravity?.mode === 'center') {
      return 'center';
    }

    return 'initial';
  }, [image]);

  const wrapperClasses = useMemo(() => {
    if (height === 'half') {
      return 'relative bg-padi-gray-darkest h-[222px] md:h-[266px] lg:h-[334px]';
    }
    return 'relative bg-padi-gray-darkest h-[296px] md:h-[532px] lg:h-[668px]';
  }, [height]);

  const contentWrapperClasses = useMemo(() => {
    const styles: { [key: string]: string } = {
      'full-top': 'pt-32 md:pt-64 lg:pt-96',
      'full-bottom': 'pb-32 md:pb-64 lg:pb-96 justify-end',
    };
    const verticalAlignClass = styles[`${height}-${verticalAlign}`] || ' justify-center';

    return `w-full h-full flex flex-col mx-auto max-w-7xl px-20 md:px-64 lg:px-32 text-${horizontalAlign} ${verticalAlignClass}`;
  }, [horizontalAlign, verticalAlign, height]);

  const buttonClasses = useMemo(() => {
    return [ctaStyle, secondCtaStyle].map((style) => {
      if (style === 'primary') {
        return `${buttonCommonClasses} bg-padi-blue hover:bg-blue-700 text-neutral-150`;
      }
      return `${buttonCommonClasses} bg-neutral-100 hover:bg-neutral-400 border-[1px] border-padi-blue  text-padi-blue`;
    });
  }, [ctaStyle, secondCtaStyle]);

  const buttonWrapperClasses = useMemo(() => {
    const justifyContent = horizontalAlign === 'center' ? 'justify-center' : 'justify-start';
    return `mt-20 flex flex-wrap items-center gap-32 md:mt-24 lg:mt-32 ${justifyContent}`;
  }, [horizontalAlign]);

  const overlayClasses = useMemo(() => {
    const baseClasses = 'absolute inset-0 h-full w-full';

    // Tailwind safelist: Ensure these classes are included in production build
    // bg-white/30 bg-white/30 bg-padi-gray-darkest/30
    // bg-gradient-to-b bg-gradient-to-t bg-gradient-to-r bg-gradient-to-l
    // from-white from-white from-padi-gray-darkest via-white/50 via-white/50 via-padi-gray-darkest/50 to-transparent
    const overlayMap = {
      none: '',
      solid: `${baseClasses} bg-${overlayColor}/30`,
      'gradient-top-down': `${baseClasses} bg-gradient-to-b from-${overlayColor} via-${overlayColor}/50 to-transparent`,
      'gradient-bottom-up': `${baseClasses} bg-gradient-to-t from-${overlayColor} via-${overlayColor}/50 to-transparent`,
      'gradient-left-right': `${baseClasses} bg-gradient-to-r from-${overlayColor} via-${overlayColor}/50 to-transparent`,
      'gradient-right-left': `${baseClasses} bg-gradient-to-l from-${overlayColor} via-${overlayColor}/50 to-transparent`,
    };

    return overlayMap[overlayType];
  }, [overlayType, overlayColor]);

  const truncateText = (text: string | undefined, maxLength: number) => {
    if (text && text.length > maxLength) {
      return text.slice(0, maxLength) + '...';
    }
    return text;
  };
  const bodyDisplay = useMemo(() => truncateText(body, 100), [body]);
  const titleDisplay = useMemo(() => truncateText(title, 56), [title]);
  const [width, setWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 0);

  const maxWidth = useMemo(() => {
    return width < 1024 ? 'none' : (width * 2) / 3;
  }, [width]);

  useEffect(() => {
    const updateWindowDimensions = () => {
      const newWidth = window.innerWidth;
      setWidth(newWidth);
    };

    window.addEventListener('resize', updateWindowDimensions);

    return () => window.removeEventListener('resize', updateWindowDimensions);
  }, []);

  return (
    <div className="relative w-full" id={sectionId}>
      <div className={wrapperClasses}>
        {image?.media && (
          <Image
            {...image}
            priority
            loading="eager"
            alt={title}
            fill
            style={{ objectFit: 'cover', objectPosition: cropFocus }}
            className={imageBrightness ? 'brightness-75' : 'filter-none'}
          />
        )}
        {youTubeLink && (
          <Video
            youTubeLink={youTubeLink}
            isLoopVideo={isLoopVideo}
            isAudio={isAudio}
            isAutoPlay={isAutoPlay}
            ytDataAnalytics={ytDataAnalytics}
          />
        )}
        {overlayType !== 'none' && <div className={overlayClasses} />}
      </div>
      <div className="absolute bottom-0 left-0 right-0 top-0 w-full">
        <div className={contentWrapperClasses}>
          <div className={horizontalAlign === 'center' ? 'mx-auto' : ''} style={{ maxWidth: maxWidth }}>
            <Typography
              as="h1"
              className={`mt-22 text-26 font-${fontWeight} text-white md:mt-32 md:text-36 lg:text-48`}
            >
              {titleDisplay}
            </Typography>
            {bodyDisplay && (
              <p className="pt-20 text-14 font-regular text-white md:text-16 lg:text-18">{bodyDisplay}</p>
            )}
            <div className={buttonWrapperClasses}>
              {ctaLabel && (
                <Link link={ctaReference}>
                  <Button className={buttonClasses[0]} padi-analytics={ctaDataAnalytics}>
                    <Typography as="span" className="text-12  md:text-14 lg:text-16">
                      {ctaLabel}
                    </Typography>
                  </Button>
                </Link>
              )}
              {secondCtaLabel && (
                <Link link={secondCtaReference}>
                  <Button className={buttonClasses[1]} padi-analytics={secondCtaDataAnalytics}>
                    <Typography as="span" className="text-12 md:text-14 lg:text-16">
                      {secondCtaLabel}
                    </Typography>
                  </Button>
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default memo(Hero);
